import { Component, effect, inject, input, OnInit, TemplateRef, viewChild, ViewChild } from '@angular/core';
import { GenericTableComponent, TableColumn } from '../../core/generic-table/generic-table.component';
import { UserOidc } from '../org-model/user-oidc';
import { catchError, defer, finalize, map, Observable, of, startWith, tap } from 'rxjs';
import { OrgAdminEndpointsService } from '../../../services/org-service/org-admin-endpoints.service';
import { MatDialog } from '@angular/material/dialog';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatInput, MatSuffix } from '@angular/material/input';
import { ButtonComponent } from '../../../common/components/button/button.component';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { TonalButtonComponent } from '../../core/tonal-button/tonal-button.component';
import { OrgOidcUsersTestIds } from './org-oidc-users.test-ids';
import { AsyncPipe } from '@angular/common';
import { LoadingService } from '../../../services/loading/loading.service';
import { SbSpinnerComponent } from '../../sb-client/sb-spinner/sb-spinner.component';
import { PasswordService } from '../../../services/password/password.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { OrgGeneralUserListComponent } from '../org-general-user-list/org-general-user-list.component';
import { passwordValidator } from '../../../validators/passwordValidator';
import { AppConfigService } from '../../../services/config/app-config.service';
import { TableSettingsComponent } from '../../core/table-settings/table-settings.component';
import { OrgUserListTestIds } from '../org-user-list/org-user-list.test-ids';
import { PasswordHint } from '../../../common/components/password-hint/password-hint';
import { copyToClipboard } from '../../../services/util';

@Component({
  selector: 'app-org-oidc-user-list.component',
  imports: [
    GenericTableComponent,
    FormsModule,
    MatInput,
    ReactiveFormsModule,
    ButtonComponent,
    TonalButtonComponent,
    AsyncPipe,
    SbSpinnerComponent,
    MatIcon,
    MatTooltip,
    MatSuffix,
    TableSettingsComponent,
    PasswordHint
  ],
  templateUrl: './org-oidc-user-list.component.html',
  styleUrl: './org-oidc-user-list.component.scss'
})

/**
 * Komponente für die Nutzerverwaltung der Keycloak-Nutzer.
 * Nutzer werden tabellarisch dargestellt.
 * Ebenfalls können Nutzer hinzugefügt und das Passwort zurückgesetzt werden.
 */
export class OrgOidcUserListComponent implements OnInit {
  searchTerm = '';
  pageIndex = 0;
  itemsPerPage = 10;
  totalItems = 0;
  isConfirmationMode = false;
  isPasswordVisible = false;
  isPasswordRepeatVisible = false;
  showSpinner = false;
  columns: TableColumn<UserOidc>[] = [];
  displayedColumns: TableColumn<UserOidc>[] = [];
  userResponse$: Observable<UserOidc[]> = of([]);
  resetPasswordForm!: FormGroup;
  selectedUser!: UserOidc;
  addUserForm!: FormGroup;
  domain = input.required<string>();

  @ViewChild('resetPassword', { static: true })
  resetPasswordTemplate!: TemplateRef<unknown>;
  readonly timAdressTemplate = viewChild.required<TemplateRef<any>>('timAdressTemplate');

  protected readonly matDialog = inject(MatDialog);
  protected readonly testIds = OrgOidcUsersTestIds;
  protected readonly userListTestIds = OrgUserListTestIds;
  protected readonly loadingService = inject(LoadingService);
  protected readonly passwordService = inject(PasswordService);
  private readonly fb = inject(FormBuilder);

  private readonly orgAdminEndpoints = inject(OrgAdminEndpointsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly appConfig = inject(AppConfigService);

  private readonly parent = inject(OrgGeneralUserListComponent);

  private readonly _searchEffect = effect(() => {
    this.searchTerm = this.parent.searchTerm();
    this.pageIndex = 0;
    this.loadOidcUsers();
  });

  get isNutzerverwaltungEnabled(): boolean {
    return this.appConfig.get('enableNutzerverwaltung') === 'true';
  }

  /**
   * Hier werden die angezeigten Error-Nachrichten bei Fehlern in der AddUser-Form strukturiert zurückgegeben.
   * Dabei wird folgende Struktur verwendet, damit nicht alle Fehler gleichzeitig angezeigt werden:
   * 1. Alle Notwendigen Felder gesetzt
   * 2. Passwort erfüllt notwendige Kriterien
   * 3. Passwort und Passwort-Wiederholen stimmen überein
   * 4. Gültige E-Mail-Adresse
   */
  get addUserErrorMessages(): string[] {
    if (!this.addUserForm.touched) return [];

    const requiredControls = ['username', 'firstname', 'lastname', 'emailAddress'];

    const anyRequiredMissing = requiredControls.some(name => this.addUserForm.get(name)?.hasError('required'));
    if (anyRequiredMissing) {
      return ['Nicht alle Felder sind ausgefüllt. Bitte füllen Sie alle Pflichtfelder aus.'];
    }

    const emailCtrl = this.addUserForm.get('emailAddress');

    if (emailCtrl?.hasError('email')) {
      return ['Bitte geben Sie eine gültige E‑Mail-Adresse ein.'];
    }

    return [];
  }

  onSortChange(): void {
    this.pageIndex = 0;
    this.loadOidcUsers();
  }

  ngOnInit(): void {
    this.columns = [
      { key: 'username', label: 'Nutzername', sortable: true },
      { key: 'matrixid', label: 'TI-M-Adresse', sortable: false, type: 'custom', template: this.timAdressTemplate() },
      { key: 'email', label: 'E-Mail Adresse', sortable: false },
      { key: 'firstName', label: 'Vorname', sortable: false },
      { key: 'lastName', label: 'Nachname', sortable: false }
    ];
    this.displayedColumns = [...this.columns];
    this.loadOidcUsers();

    this.resetPasswordForm = this.fb.group(
      {
        password: ['', [passwordValidator(), Validators.required]],
        passwordRepeat: ['', Validators.required]
      },
      {
        validators: this.passwordsMatchValidator('password', 'passwordRepeat')
      }
    );
    this.addUserForm = this.fb.group(
      {
        username: ['', [Validators.required]],
        initialPassword: ['', [passwordValidator(), Validators.required]],
        initialPasswordRepeat: ['', [Validators.required]],
        firstname: ['', [Validators.required]],
        lastname: ['', [Validators.required]],
        emailAddress: ['', [Validators.required, Validators.email]]
      },
      {
        validators: this.passwordsMatchValidator('initialPassword', 'initialPasswordRepeat')
      }
    );
  }

  onPageChange(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.loadOidcUsers();
  }

  submitPassword(): void {
    const body = {
      userId: this.selectedUser.id,
      username: this.selectedUser.username,
      initialPassword: this.resetPasswordForm.value.password
    };

    if (body.userId && body.username) {
      this.orgAdminEndpoints.resetOidcUserPassword(this.domain(), body).subscribe({
        next: () => {
          this.resetPasswordForm.reset();
        },
        error: () => {
          this.snackbarService.error('Fehler beim Zurücksetzen');
        },
        complete: () => {
          this.matDialog.closeAll();
          this.resetPasswordForm.reset();
          this.snackbarService.success('Passwort für ' + body.username + ' wurde zurückgesetzt');
        }
      });
    }
  }

  submitAddUser(): void {
    if (!this.isNutzerverwaltungEnabled) {
      return;
    }

    const body = {
      username: this.addUserForm.value.username,
      initialPassword: this.addUserForm.value.initialPassword,
      firstname: this.addUserForm.value.firstname,
      lastname: this.addUserForm.value.lastname,
      emailAddress: this.addUserForm.value.emailAddress
    };

    this.showSpinner = true;
    this.orgAdminEndpoints.addOidcUser(this.domain(), body).subscribe({
      next: () => {
        this.addUserForm.reset();
      },
      error: () => {
        this.snackbarService.error('Fehler beim Erstellen eines neuen Benutzers');
      },
      complete: () => {
        this.matDialog.closeAll();
        this.isConfirmationMode = false;
        this.showSpinner = false;
        this.snackbarService.success('Neuer Benutzer wurde erstellt');
        this.loadOidcUsers();
      }
    });
  }

  continueAddUser(): void {
    if (this.addUserForm.invalid) return;

    this.isConfirmationMode = true;
  }

  suggestPassword(): void {
    this.resetPasswordForm.patchValue({
      password: this.passwordService.generatePassword()
    });
  }

  openResetPasswordDialog(user: UserOidc): void {
    if (!this.isNutzerverwaltungEnabled) {
      return;
    }
    this.selectedUser = user;
    this.resetPasswordForm.reset();
    this.isPasswordVisible = false;
    this.matDialog.open(this.resetPasswordTemplate);
  }

  async copySuggestedPassword(): Promise<void> {
    const password = this.resetPasswordForm.value.password;
    if (!password) {
      this.snackbarService.error('Kein Passwort zum Kopieren vorhanden');
      return;
    }
    await copyToClipboard(
      password,
      msg => this.snackbarService.success(msg!),
      msg => this.snackbarService.error(msg!),
      'Passwort kopiert',
      'Passwort konnte nicht kopiert werden'
    );
  }

  private loadOidcUsers(): void {
    this.userResponse$ = defer(() => {
      this.loadingService.start();

      const queryParams: Record<string, string | number | boolean | undefined> = {
        search: this.searchTerm,
        first: this.pageIndex * this.itemsPerPage,
        max: this.itemsPerPage
      };

      return this.orgAdminEndpoints.getOidcUsers(this.domain(), queryParams).pipe(
        tap(resp => {
          this.totalItems = resp.totalCount;
        }),
        map(response => response.userList ?? []),

        map(users =>
          users.map(user => ({
            ...user,
            matrixid: user.attributes?.matrixid
          }))
        ),

        startWith([] as UserOidc[]),

        catchError(() => {
          this.snackbarService.error('Es konnten keine User geladen werden');
          return of([] as UserOidc[]);
        }),
        finalize(() => {
          this.loadingService.stop();
        })
      );
    });
  }

  private passwordsMatchValidator(passwordKey: string, repeatKey: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.get(passwordKey)?.value;
      const passwordRepeat = control.get(repeatKey)?.value;
      return password === passwordRepeat ? null : { passwordsMismatch: true };
    };
  }
}
